import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Flex, Typo, theme } from '../../../../ui';
import Icon from '../../../../ui/Icon';
import { useSettingsObject } from '../../../settings/hooks';
import { getUserThemeColor, shadeColor, toRgba } from '../../../../ui/theme/colors';
import { useInvitesActions } from '../../../invites/hooks/useInvitesActions';
import CreateCourseModel from '../../../course/components/CreateCourseModel';

const WidgetContainer = styled(Flex)`
  background: ${theme.colors.NEU_0};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px ${toRgba(theme.colors.NEU_999, 0.04)};
  width: 100%;
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  width: 100%;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${(props) => props.$lightColor};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    background: ${(props) => toRgba(props.$brandColor, 0.2)};
    transform: translateY(-2px);
  }
`;

const ActionIconWrapper = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$brandColor};
  flex-shrink: 0;
`;

/**
 * QuickActionsWidget Component
 *
 * Common teacher actions in one place:
 * - Invite students to course (opens SendInvite modal)
 * - Create new course (opens CreateCourseModel)
 */
const QuickActionsWidget = () => {
  const navigate = useNavigate();
  const settings = useSettingsObject();
  const userTheme = settings?.theme;
  const brandColor = userTheme?.color ? getUserThemeColor(userTheme?.color) : theme.colors.PRIMARY;
  const lightColor = userTheme?.color ? shadeColor(userTheme?.color, 0.92) : theme.colors.NEU_50;

  const { openInvitesModal } = useInvitesActions();
  const [isCreateCourseOpen, setIsCreateCourseOpen] = useState(false);

  const handleOpenInviteModal = useCallback(() => {
    openInvitesModal({});
  }, [openInvitesModal]);

  const handleOpenCreateCourse = useCallback(() => {
    setIsCreateCourseOpen(true);
  }, []);

  const handleCloseCreateCourse = useCallback(() => {
    setIsCreateCourseOpen(false);
  }, []);

  const handleCourseCreated = useCallback(
    (course) => {
      setIsCreateCourseOpen(false);
      if (course?.id) {
        navigate(`/courses/${course.id}/edit`);
      }
    },
    [navigate],
  );

  const actions = [
    {
      id: 'invite',
      label: 'Invite Students',
      description: 'Send course invitations',
      icon: 'Invite',
      onClick: handleOpenInviteModal,
    },
    {
      id: 'create',
      label: 'Create Course',
      description: 'Start a new course',
      icon: 'Add',
      onClick: handleOpenCreateCourse,
    },
    {
      id: 'tasks',
      label: 'View All Tasks',
      description: 'See all submissions',
      icon: 'Task',
      onClick: null,
    },
    {
      id: 'groups',
      label: 'Manage Groups',
      description: 'Organize your classes',
      icon: 'Users',
      onClick: null,
    },
  ];

  return (
    <>
      <WidgetContainer direction='column' align='flex-start'>
        <Flex align='center' justify='space-between' width='100%' style={{ marginBottom: '20px' }}>
          <Flex direction='column' align='flex-start'>
            <Typo.ContentSmallHeader $textAlign='left' color={theme.colors.NEU_PRIMARY}>
              Quick Actions
            </Typo.ContentSmallHeader>
            <Typo.Info color={theme.colors.NEU_600}>Common tasks at your fingertips</Typo.Info>
          </Flex>
          <Icon.Bolt size={24} color={brandColor} />
        </Flex>

        <ActionsGrid>
          {actions.map((action) => {
            const ActionIcon = Icon[action.icon];
            return (
              <ActionButton key={action.id} onClick={action.onClick} $brandColor={brandColor} $lightColor={lightColor}>
                <ActionIconWrapper $brandColor={brandColor}>{ActionIcon && <ActionIcon size={'small'} color={theme.colors.NEU_0} />}</ActionIconWrapper>
                <Flex direction='column' align='flex-start'>
                  <Typo.TextTitle fontWeight='600' color={theme.colors.NEU_PRIMARY}>
                    {action.label}
                  </Typo.TextTitle>
                  <Typo.Info color={theme.colors.NEU_600}>{action.description}</Typo.Info>
                </Flex>
              </ActionButton>
            );
          })}
        </ActionsGrid>
      </WidgetContainer>

      <CreateCourseModel open={isCreateCourseOpen} onClose={handleCloseCreateCourse} onSuccess={handleCourseCreated} />
    </>
  );
};

export default QuickActionsWidget;
